import type {EditorState} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'

/** Returns true if there is actual selected text in the editor. */
export function hasNonEmptySelection(state: EditorState): boolean {
  return state.selection.ranges.length === 1 && !state.selection.main.empty
}

export function expandRangeToLineEnds(state: EditorState) {
  const caretLocation = state.selection.main.anchor
  const document = state.doc.toString()

  const lineFrom = document.slice(0, caretLocation).lastIndexOf('\n') + 1
  let lineTo = document.indexOf('\n', caretLocation)
  if (lineTo === -1) lineTo = document.length

  return {from: lineFrom, to: lineTo}
}

/**
 * Find word boundaries around a given position
 *
 * Treats markdown delimiters and common punctuation as word boundaries, similar to how
 * text formatting works. Apostrophes are only treated as boundaries at the start/end of
 * words, not in the middle, allowing proper selection of contractions and possessives.
 * Hyphens are always treated as word boundaries.
 */
export function findWordBoundaries(text: string, position: number): {start: number; end: number} {
  let start = position
  let end = position

  // Helper to check if a character is a word boundary
  // Includes markdown delimiters, brackets, and common punctuation
  // Apostrophes are handled specially below
  const isBoundary = (char: string) => /[^\w\d']/.test(char)

  // Find word start by moving backwards
  while (start > 0 && text[start - 1]) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const char = text[start - 1]!
    // Apostrophe at the start is a boundary (e.g., "'hello" → stops at ')
    if (char === "'") {
      // Check if there's a word character before the apostrophe
      const prevChar = text[start - 2]
      if (!prevChar || isBoundary(prevChar) || /\s/.test(prevChar)) {
        break // Apostrophe at start of word, stop here
      }
    }
    if (isBoundary(char)) break
    start--
  }

  // Find word end by moving forwards
  while (end < text.length && text[end]) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const char = text[end]!
    // Apostrophe at the end is a boundary (e.g., "hello'" → stops at ')
    if (char === "'") {
      // Check if there's a word character after the apostrophe
      const nextChar = text[end + 1]
      if (!nextChar || isBoundary(nextChar) || /\s/.test(nextChar)) {
        break // Apostrophe at end of word, stop here
      }
    }
    if (isBoundary(char)) break
    end++
  }

  return {start, end}
}

export function getCaretPosition(state: EditorState): number | null {
  return state.selection.ranges.length === 1 && state.selection.main.empty ? state.selection.main.anchor : null
}

/**
 * Get accurate coordinates for a caret position, handling decorated blocks correctly.
 * Falls back to CodeMirror's `coordsAtPos` if DOM range approach fails.
 */
export const getAccurateCaretCoords = (
  view: EditorView,
  pos: number,
): {left: number; top: number; right: number; bottom: number} | null => {
  try {
    // Try using DOM range for more accurate positioning in decorated blocks
    const domPos = view.domAtPos(pos)
    if (domPos?.node) {
      try {
        const range = document.createRange()
        range.setStart(domPos.node, domPos.offset)
        range.setEnd(domPos.node, domPos.offset)
        const rect = range.getBoundingClientRect()

        // Return rect if it has valid coordinates (not all zeros from hidden nodes)
        if (rect.left !== 0 || rect.right !== 0 || rect.top !== 0 || rect.bottom !== 0) {
          return {left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom}
        }
      } catch {
        // range.setStart/setEnd throws if offset is outside the node size
      }
    }

    // Fall back to CodeMirror's coordsAtPos method
    return view.coordsAtPos(pos) ?? null
  } catch {
    return null
  }
}
